// @ts-nocheck
/**
 * VoiceStudio - 音色配置管理组件
 *
 * 功能：
 * - 查看可用音色列表
 * - 设置默认音色
 * - 调整音色参数（语速、音量、音调）
 */

import React, { useState, useEffect } from 'react';
import { apiGet, apiPut } from '../../utils/apiClient';

interface VoiceStudioProps {
  onRefresh?: () => void;
}

interface VoiceSettings {
  speed: number;
  vol: number;
  pitch: number;
}

const CATEGORIES = [
  { id: 'all', label: '全部' },
  { id: 'female', label: '女声' },
  { id: 'male', label: '男声' },
  { id: 'child', label: '童声' },
];

export default function VoiceStudio({ onRefresh }: VoiceStudioProps) {
  const [voices, setVoices] = useState<any[]>([]);
  const [currentVoice, setCurrentVoice] = useState<any>(null);
  const [selectedVoice, setSelectedVoice] = useState<any>(null);
  const [settings, setSettings] = useState<VoiceSettings>({ speed: 1.0, vol: 1.0, pitch: 0 });
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [activeCategory, setActiveCategory] = useState<string>('all');

  useEffect(() => {
    fetchVoices();
    fetchCurrentVoice();
  }, []);

  const fetchVoices = async () => {
    try {
      setLoading(true);
      const response = await apiGet('/api/admin/config/voices') as any;
      if (Array.isArray(response)) {
        setVoices(response);
      }
    } catch (error: any) {
      console.error('获取音色列表失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentVoice = async () => {
    try {
      const response = await apiGet('/api/admin/config/voices/current') as any;
      if (response.success) {
        setCurrentVoice(response.voice);
        setSettings({
          speed: response.voice.speed || 1.0,
          vol: response.voice.vol || 1.0,
          pitch: response.voice.pitch || 0
        });
      }
    } catch (error: any) {
      console.error('获取当前音色失败:', error);
    }
  };

  const handleSetDefault = async (voiceId: string) => {
    try {
      const response = await apiPut(`/api/admin/config/voices/${voiceId}/default`) as any;
      if (response.success) {
        alert('默认音色设置成功');
        fetchVoices();
        fetchCurrentVoice();
        onRefresh?.();
      } else {
        alert('设置失败: ' + response.message);
      }
    } catch (error: any) {
      console.error('设置默认音色失败:', error);
      alert('设置失败');
    }
  };

  const handleSaveSettings = async () => {
    if (!currentVoice) return;

    try {
      setSaving(true);
      const response = await apiPut(
        `/api/admin/config/voices/${currentVoice.voice_id}/settings`,
        settings
      ) as any;

      if (response.success) {
        alert('参数保存成功');
        onRefresh?.();
      } else {
        alert('保存失败: ' + response.message);
      }
    } catch (error: any) {
      console.error('保存设置失败:', error);
      alert('保存失败');
    } finally {
      setSaving(false);
    }
  };

  const getCategoryIcon = (category: string): string => {
    const icons: Record<string, string> = {
      female: '👩',
      male: '👨',
      child: '🧒',
      default: '🎙️'
    };
    return icons[category] || icons.default;
  };

  const filteredVoices = activeCategory === 'all'
    ? voices
    : voices.filter((v: any) => v.category === activeCategory);

  if (loading) {
    return <div className="loading-spinner"></div>;
  }

  return (
    <div className="voice-studio">
      {/* 当前音色设置 */}
      {currentVoice && (
        <div className="admin-panel">
          <div className="panel-header">
            <h3 className="panel-title">🎙️ 当前音色</h3>
            <button
              className="btn-primary"
              onClick={handleSaveSettings}
              disabled={saving}
            >
              {saving ? '保存中...' : '保存参数'}
            </button>
          </div>

          <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start' }}>
            {/* 音色信息 */}
            <div style={{
              padding: '20px',
              background: 'rgba(0,245,212,0.05)',
              border: '1px solid rgba(0,245,212,0.2)',
              borderRadius: '12px',
              minWidth: '200px'
            }}>
              <div style={{ fontSize: '32px', marginBottom: '8px' }}>
                {getCategoryIcon(currentVoice.category)}
              </div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#fff' }}>
                {currentVoice.name}
              </div>
              <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginTop: '4px' }}>
                {currentVoice.provider}
              </div>
              {currentVoice.description && (
                <div style={{
                  fontSize: '13px',
                  color: 'rgba(255,255,255,0.6)',
                  marginTop: '12px',
                  lineHeight: '1.5'
                }}>
                  {currentVoice.description}
                </div>
              )}
            </div>

            {/* 参数调整 */}
            <div style={{ flex: 1 }}>
              {/* 语速 */}
              <div className="form-group">
                <label className="form-label">语速 (0.5 - 2.0)</label>
                <div className="slider-control">
                  <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>慢</span>
                  <input
                    type="range"
                    min="0.5"
                    max="2"
                    step="0.1"
                    value={settings.speed}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSettings({ ...settings, speed: parseFloat(e.target.value) })}
                  />
                  <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>快</span>
                  <span className="slider-value">{settings.speed.toFixed(1)}x</span>
                </div>
              </div>

              {/* 音量 */}
              <div className="form-group">
                <label className="form-label">音量 (0.5 - 1.5)</label>
                <div className="slider-control">
                  <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>低</span>
                  <input
                    type="range"
                    min="0.5"
                    max="1.5"
                    step="0.1"
                    value={settings.vol}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSettings({ ...settings, vol: parseFloat(e.target.value) })}
                  />
                  <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>高</span>
                  <span className="slider-value">{settings.vol.toFixed(1)}</span>
                </div>
              </div>

              {/* 音调 */}
              <div className="form-group">
                <label className="form-label">音调 (-12 - 12)</label>
                <div className="slider-control">
                  <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>低沉</span>
                  <input
                    type="range"
                    min="-12"
                    max="12"
                    step="1"
                    value={settings.pitch}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSettings({ ...settings, pitch: parseInt(e.target.value) })}
                  />
                  <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>尖锐</span>
                  <span className="slider-value">{settings.pitch > 0 ? '+' : ''}{settings.pitch}</span>
                </div>
              </div>

              {/* 重置按钮 */}
              <button
                className="btn-secondary"
                onClick={() => setSettings({ speed: 1.0, vol: 1.0, pitch: 0 })}
                style={{ marginTop: '8px' }}
              >
                重置为默认
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 音色库 */}
      <div className="admin-panel">
        <div className="panel-header">
          <h3 className="panel-title">🎭 音色库</h3>
          <div className="category-tabs">
            {CATEGORIES.map(cat => (
              <button
                key={cat.id}
                className={`category-tab ${activeCategory === cat.id ? 'active' : ''}`}
                onClick={() => setActiveCategory(cat.id)}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>

        <div className="card-grid">
          {filteredVoices.map((voice: any) => (
            <div
              key={voice.voice_id}
              className={`config-card ${voice.is_default ? 'default' : ''}`}
              onClick={() => setSelectedVoice(voice)}
            >
              <div className="config-card-header">
                <span className="config-card-title">
                  {getCategoryIcon(voice.category)} {voice.name}
                </span>
                {voice.is_default && (
                  <span className="badge badge-success">默认</span>
                )}
              </div>
              <div className="config-card-desc">
                {voice.description || voice.voice_id}
              </div>
              <div className="config-card-meta">
                <span>{voice.provider}</span>
                <span className="badge badge-info">{voice.category || '通用'}</span>
              </div>

              {!voice.is_default && (
                <button
                  className="btn-secondary"
                  style={{ marginTop: '12px', width: '100%' }}
                  onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation();
                    handleSetDefault(voice.voice_id);
                  }}
                >
                  设为默认
                </button>
              )}
            </div>
          ))}
        </div>

        {filteredVoices.length === 0 && (
          <div className="empty-state">
            <div className="empty-state-icon">🎙️</div>
            <div className="empty-state-text">
              {activeCategory === 'all' ? '暂无可用音色' : `暂无 ${CATEGORIES.find(c => c.id === activeCategory)?.label} 音色`}
            </div>
          </div>
        )}
      </div>

      <style>{`
        .category-tabs {
          display: flex;
          gap: 8px;
        }
        .category-tab {
          padding: 6px 12px;
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 6px;
          color: rgba(255,255,255,0.6);
          cursor: pointer;
          transition: all 0.2s;
          font-size: 13px;
        }
        .category-tab:hover {
          background: rgba(255,255,255,0.1);
          color: #fff;
        }
        .category-tab.active {
          background: rgba(0,245,212,0.1);
          border-color: rgba(0,245,212,0.3);
          color: #00f5d4;
        }
        .config-card.default {
          border-color: rgba(16,185,129,0.5);
          background: rgba(16,185,129,0.05);
        }
      `}</style>
    </div>
  );
}
